import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, defer } from 'rxjs';
import { environment } from '../../../../../../../projects/my-app/src/environments/environment';

const AGENT_API_URL = environment.agentApiUrl;

export interface ChatFigure {
  id: string;
  url: string;
  caption?: string;
  page?: number | null;
  node_id?: string;
}

export type ChatStreamEvent =
  | {
      kind: 'meta';
      conversation_id?: string | null;
      in_scope?: boolean;
      nodes?: any[];
      figures?: ChatFigure[];
      trace?: any;
      scope_message?: string | null;
    }
  | { kind: 'token'; text: string }
  | {
      kind: 'done';
      conversation_id?: string | null;
      answer?: string;
      answer_format?: string;
      figures?: ChatFigure[];
      latency_ms?: number;
      trace?: any;
      scope_message?: string | null;
    }
  | { kind: 'error'; code: string; message: string };

@Injectable({
  providedIn: 'root',
})
export class ChatApiService {
  constructor(protected http: HttpClient) {}

  async sendMessage(
    query: string,
    conversationId?: string | null,
    apiUrl?: string,
    includeTrace = true,
  ): Promise<Observable<any>> {
    const requestPayload = this.getRequestPayload(query, conversationId, includeTrace);
    return this.http.post<any>(this.getBaseUrl(apiUrl), requestPayload);
  }

  protected getRequestPayload(
    query: string,
    conversationId?: string | null,
    includeTrace = true,
  ): any {
    return {
      query,
      conversation_id: conversationId || null,
      include_trace: includeTrace,
    };
  }

  streamMessage(
    query: string,
    conversationId?: string | null,
    apiUrl?: string,
    includeTrace = true,
  ): Observable<ChatStreamEvent> {
    const requestPayload = this.getRequestPayload(query, conversationId, includeTrace);

    return defer(() => {
      const subject = new Subject<ChatStreamEvent>();
      const controller = new AbortController();

      this.runSseRequest(requestPayload, apiUrl, controller.signal, subject).catch((error) =>
        subject.error(error),
      );

      return new Observable<ChatStreamEvent>((subscriber) => {
        const innerSubscription = subject.subscribe(subscriber);
        return () => {
          controller.abort();
          innerSubscription.unsubscribe();
        };
      });
    });
  }

  protected getBaseUrl(apiUrl?: string): string {
    let baseUrl = AGENT_API_URL;
    if (apiUrl) {
      if (apiUrl.startsWith('http')) {
        baseUrl = apiUrl;
      } else {
        const base = AGENT_API_URL.endsWith('/') ? AGENT_API_URL.slice(0, -1) : AGENT_API_URL;
        const path = apiUrl.startsWith('/') ? apiUrl : `/${apiUrl}`;
        baseUrl = `${base}${path}`;
      }
    }
    return baseUrl;
  }

  protected getStreamUrl(apiUrl?: string): string {
    const baseUrl = this.getBaseUrl(apiUrl);
    return baseUrl.endsWith('/stream') ? baseUrl : `${baseUrl.replace(/\/$/, '')}`;
  }

  resolveAssetUrl(url: string, apiUrl?: string): string {
    if (!url) {
      return url;
    }

    if (/^https?:\/\//i.test(url)) {
      return url;
    }

    return new URL(url, this.getBaseUrl(apiUrl)).toString();
  }

  private async runSseRequest(
    body: any,
    apiUrl: string | undefined,
    signal: AbortSignal,
    output: Subject<ChatStreamEvent>,
  ): Promise<void> {
    const response = await this.fetchSseResponse(body, apiUrl, signal, output);
    if (!response?.body) {
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        let separatorIndex = buffer.indexOf('\n\n');

        while (separatorIndex !== -1) {
          const frame = buffer.slice(0, separatorIndex);
          buffer = buffer.slice(separatorIndex + 2);
          const event = this.parseSseFrame(frame);

          if (event) {
            output.next(event);
            if (event.kind === 'done' || event.kind === 'error') {
              output.complete();
              try {
                await reader.cancel();
              } catch {}
              return;
            }
          }

          separatorIndex = buffer.indexOf('\n\n');
        }
      }

      output.complete();
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        output.complete();
        return;
      }

      output.error(error instanceof Error ? error : new Error(String(error)));
    }
  }

  private async fetchSseResponse(
    body: any,
    apiUrl: string | undefined,
    signal: AbortSignal,
    output: Subject<ChatStreamEvent>,
  ): Promise<Response | null> {
    try {
      const response = await fetch(this.getStreamUrl(apiUrl), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        },
        body: JSON.stringify(body),
        signal,
      });

      if (!response.ok || !response.body) {
        const detail = await this.readErrorResponse(response);
        output.error(new Error(`HTTP ${response.status}: ${detail || response.statusText}`));
        return null;
      }

      return response;
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        output.complete();
        return null;
      }

      output.error(new Error(`Network error contacting chat API: ${error?.message ?? error}`));
      return null;
    }
  }

  private async readErrorResponse(response: Response): Promise<string> {
    try {
      const json = await response.json();
      return json?.error?.message || json?.message || '';
    } catch {
      try {
        return await response.text();
      } catch {
        return '';
      }
    }
  }

  private parseSseFrame(frame: string): ChatStreamEvent | null {
    if (!frame.trim()) {
      return null;
    }

    let eventName = 'message';
    const dataLines: string[] = [];

    for (const line of frame.split('\n')) {
      if (!line || line.startsWith(':')) {
        continue;
      }

      if (line.startsWith('event:')) {
        eventName = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trim());
      }
    }

    if (!dataLines.length) {
      return null;
    }

    const rawData = dataLines.join('\n');
    let payload: any;

    try {
      payload = JSON.parse(rawData);
    } catch {
      return null;
    }

    switch (eventName) {
      case 'meta':
        return { kind: 'meta', ...payload };
      case 'token':
        return { kind: 'token', text: String(payload?.text ?? '') };
      case 'done':
        return { kind: 'done', ...payload };
      case 'error':
        return {
          kind: 'error',
          code: payload?.code ?? 'error',
          message: payload?.message ?? 'Unknown error',
        };
      default:
        return null;
    }
  }
}
